import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import {
  FiServer,
  FiLayers,
  FiUsers,
  FiTrendingUp,
  FiGitBranch,
  FiCloud,
  FiDatabase,
  FiCode,
} from 'react-icons/fi';
import './Skills.css';

const Skills = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const skillCategories = [
    {
      title: 'Leadership',
      icon: <FiUsers />,
      skills: [
        'Team Building',
        'Mentorship',
        'Strategic Planning',
        'Stakeholder Management',
        'Cross-functional Collaboration',
      ],
    },
    {
      title: 'Engineering',
      icon: <FiCode />,
      skills: [
        'System Design',
        'Architecture',
        'Code Review',
        'Technical Strategy',
        'Best Practices',
      ],
    },
    {
      title: 'Operations',
      icon: <FiServer />,
      skills: [
        'DevOps',
        'CI/CD',
        'Process Optimization',
        'Delivery Management',
        'Resource Planning',
      ],
    },
    {
      title: 'Product',
      icon: <FiLayers />,
      skills: [
        'Product Strategy',
        'Roadmap Planning',
        'Requirements Analysis',
        'User Research',
        'Agile/Scrum',
      ],
    },
  ];

  const technologies = [
    { name: 'JavaScript/TypeScript', icon: <FiCode /> },
    { name: 'React/Node.js', icon: <FiLayers /> },
    { name: 'Python', icon: <FiCode /> },
    { name: 'AWS/Cloud', icon: <FiCloud /> },
    { name: 'PostgreSQL/MongoDB', icon: <FiDatabase /> },
    { name: 'Git/CI-CD', icon: <FiGitBranch /> },
    { name: 'Kubernetes/Docker', icon: <FiServer /> },
    { name: 'Microservices', icon: <FiTrendingUp /> },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section id="skills" className="skills section" ref={ref}>
      <div className="container">
        <motion.div
          className="skills-header"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">Skills & Expertise</h2>
          <p className="section-subtitle">
            Areas of expertise developed through years of experience
          </p>
        </motion.div>

        <motion.div
          className="skills-grid"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {skillCategories.map((category, index) => (
            <motion.div
              key={index}
              className="skill-card card"
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
            >
              <div className="skill-card-icon">{category.icon}</div>
              <h3 className="skill-card-title">{category.title}</h3>
              <ul className="skill-list">
                {category.skills.map((skill, i) => (
                  <li key={i}>{skill}</li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="technologies-section"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3 className="technologies-title">Technologies & Tools</h3>
          <motion.div
            className="technologies-grid"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            {technologies.map((tech, index) => (
              <motion.div
                key={index}
                className="tech-badge"
                variants={itemVariants}
                whileHover={{ scale: 1.1, y: -5 }}
              >
                {tech.icon}
                <span>{tech.name}</span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
